// react
import React, { useState, useRef } from 'react';

// utils
import { translate } from '../../utils/translator';
import useIsMobile from '../../customHooks/useIsMobile';
import { combineDateAndTime, formatDateStamp } from '../../utils/timestampFormatter';

// components
import Skeletonize from '../../Skeletons/Skeletonize';
import ScrollableTimeSelector from '../../components/ScrollableTimeSelector/ScrollableTimeSelector';
import Select from '../../components/Select/Select';

type Props = {
	language: string;
	onTimeSelection: (timeSlot: {start: string, end: string}) => void;
	selectedTime: {start: string, end: string} | null;
	selectedDate: string | null;
	additionalClassName?: string;
	isLoading?: boolean;
}

type TimeRangeOption = {
	label: string;
	start: string;
	end: string;
}

const PreferredTimeSelector: React.FC<Props> = ({ 
	language, 
	onTimeSelection, 
	selectedTime,
	selectedDate,
	additionalClassName,
	isLoading = false 
}) => {
	const [activeTab, setActiveTab] = useState<'time_range' | 'set_time'>('time_range');
	const isMobile = useIsMobile();
	const cardContainerRef = useRef<HTMLDivElement>(null);
	
	// Set Time state
	const [fromHour, setFromHour] = useState<string>('09');
	const [fromMinute, setFromMinute] = useState<string>('00');
	const [toHour, setToHour] = useState<string>('10');
	const [toMinute, setToMinute] = useState<string>('30');

	// Generate time range options (9:00-10:00, 10:00-11:00, ..., 21:00-22:00)
	// Regenerate when selectedDate changes
	const timeRangeOptions: TimeRangeOption[] = React.useMemo(() => {
		const options: TimeRangeOption[] = [];
		for (let hour = 9; hour <= 21; hour++) {
			const startHour = hour.toString().padStart(2, '0');
			const endHour = (hour + 1).toString().padStart(2, '0');
			options.push({
				label: `${startHour}:00 - ${endHour}:00`,
				start: combineDateAndTime(selectedDate, `${startHour}:00:00`),
				end: combineDateAndTime(selectedDate, `${endHour}:00:00`)
			});
		}
		
		// Add flexible option
		options.push({
			label: translate(language, 'choose_doctor', 'preferred_time/time_range/flexible_option'),
			start: combineDateAndTime(selectedDate, '09:00:00'),
			end: combineDateAndTime(selectedDate, '22:00:00')
		});
		
		return options;
	}, [selectedDate, language]);

	// Generate hour options (09-22)
	const hourOptions = Array.from({ length: 14 }, (_, i) => (i + 9).toString().padStart(2, '0'));
	
	// Minute options (00, 30)
	const minuteOptions = ['00', '30'];

	const formatTimeForComparison = (hour: string, minute: string): number => {
		return parseInt(hour) * 60 + parseInt(minute);
	};

	const createTimeString = (hour: string, minute: string): string => {
		return combineDateAndTime(selectedDate, `${hour}:${minute}:00`);
	};

	// Calculates adjusted "to" time when "from" time changes
	const getAdjustedToTimeForFrom = (fromHour: string, fromMinute: string, currentToHour: string, currentToMinute: string): { hour: string, minute: string } => {
		const fromTime = formatTimeForComparison(fromHour, fromMinute);
		const toTime = formatTimeForComparison(currentToHour, currentToMinute);
		
		if (fromTime >= toTime) {
			if (fromHour === '22' && fromMinute === '00') {
				return { hour: '22', minute: '30' };
			} else {
				const newToHour = Math.min(parseInt(fromHour) + 1, 22);
				const newToMinute = newToHour === 22 ? '00' : fromMinute;
				return { hour: newToHour.toString().padStart(2, '0'), minute: newToMinute };
			}
		}
		
		return { hour: currentToHour, minute: currentToMinute };
	};

	// Calculates adjusted "to" time when "to" time changes, handles x:00 case
	const getAdjustedToTimeForTo = (toHour: string, toMinute: string, fromHour: string, fromMinute: string): { hour: string, minute: string } | null => {
		if (fromHour && fromMinute === '00' && toHour === fromHour) {
			return { hour: toHour, minute: '30' };
		}
		
		if (fromHour && fromMinute) {
			const fromTime = formatTimeForComparison(fromHour, fromMinute);
			const toTime = formatTimeForComparison(toHour, toMinute);
			if (toTime > fromTime) {
				return { hour: toHour, minute: toMinute };
			}
			return null;
		}
		
		return { hour: toHour, minute: toMinute };
	};

	// Checks if time range is valid
	const isValidTimeRange = (fromHour: string, fromMinute: string, toHour: string, toMinute: string): boolean => {
		if (!fromHour || !fromMinute || !toHour || !toMinute) return false;
		return formatTimeForComparison(fromHour, fromMinute) < formatTimeForComparison(toHour, toMinute);
	};

	// Creates time slot object with start and end times
	const buildTimeSlot = (fromHour: string, fromMinute: string, toHour: string, toMinute: string) => {
		return {
			start: createTimeString(fromHour, fromMinute),
			end: createTimeString(toHour, toMinute)
		};
	};

	const handleSelectedTimeChange = (type: 'from' | 'to', hour: string, minute: string) => {
		if (type === 'from') {
			setFromHour(hour);
			setFromMinute(minute);
			const adjustedTo = getAdjustedToTimeForFrom(hour, minute, toHour, toMinute);
			setToHour(adjustedTo.hour);
			setToMinute(adjustedTo.minute);
		} else {
			const adjustedTo = getAdjustedToTimeForTo(hour, minute, fromHour, fromMinute);
			if (adjustedTo) {
				setToHour(adjustedTo.hour);
				setToMinute(adjustedTo.minute);
			}
		}
	};

	const handleFromTimeChange = (newHour: string, newMinute: string) => {
		handleSelectedTimeChange('from', newHour, newMinute);
	};

	const handleToTimeChange = (newHour: string, newMinute: string) => {
		handleSelectedTimeChange('to', newHour, newMinute);
	};

	// Desktop handlers that auto-update schedule overview
	const handleDesktopFromTimeChange = (newHour: string, newMinute: string) => {
		const adjustedTo = getAdjustedToTimeForFrom(newHour, newMinute, toHour, toMinute);
		handleSelectedTimeChange('from', newHour, newMinute);
		if (adjustedTo.hour && adjustedTo.minute) {
			onTimeSelection(buildTimeSlot(newHour, newMinute, adjustedTo.hour, adjustedTo.minute));
		}
	};

	const handleDesktopToTimeChange = (newHour: string, newMinute: string) => {
		const adjustedTo = getAdjustedToTimeForTo(newHour, newMinute, fromHour, fromMinute);
		if (adjustedTo && fromHour && fromMinute) {
			handleSelectedTimeChange('to', adjustedTo.hour, adjustedTo.minute);
			onTimeSelection(buildTimeSlot(fromHour, fromMinute, adjustedTo.hour, adjustedTo.minute));
		}
	};

	const isSetTimeValid = (): boolean => {
		return isValidTimeRange(fromHour, fromMinute, toHour, toMinute);
	};

	// Handle tab change
	const handleTabChange = (tab: 'time_range' | 'set_time') => {
		setActiveTab(tab);
		// On desktop, auto-update summary when switching to "Set Time" tab
		if (tab === 'set_time' && isSetTimeValid() && !isMobile) {
			onTimeSelection(buildTimeSlot(fromHour, fromMinute, toHour, toMinute));
		}
	};

	const getTimePortion = (datetime: string): string => {
		return datetime.includes('T') ? datetime.split('T')[1] : datetime;
	};

	const isTimeRangeSelected = (option: TimeRangeOption): boolean => {
		if (!selectedTime) return false;
		return getTimePortion(selectedTime.start) === getTimePortion(option.start) && 
		       getTimePortion(selectedTime.end) === getTimePortion(option.end);
	};

	// Handle time range selection
	const handleTimeRangeSelection = (option: TimeRangeOption) => {
		onTimeSelection({ start: option.start, end: option.end });
	};

	const getAvailableToHours = (): string[] => {
		if (!fromHour || !fromMinute) return [];
		const fromTime = formatTimeForComparison(fromHour, fromMinute);
		// Include the same hour if we can select 30 minutes later, or any hour after
		return hourOptions.filter(hour => {
			// Special case: if from is 22:00, allow hour 22 for 22:30
			if (hour === '22' && fromHour === '22' && fromMinute === '00') {
				return true;
			}
			// Allow same hour if from minute is 00 (so we can select :30)
			if (hour === fromHour && fromMinute === '00') {
				return true;
			}
			// Allow any hour where :00 is greater than from time
			return formatTimeForComparison(hour, '00') > fromTime;
		});
	};

	const getAvailableToMinutes = (): string[] => {
		if (!fromHour || !fromMinute || !toHour) return [];
		// If from is 22:00 and until is 22, only allow 30
		if (fromHour === '22' && fromMinute === '00' && toHour === '22') {
			return ['30'];
		}
		// If same hour, only allow minutes that are at least 30 minutes after from
		if (toHour === fromHour) {
			// If from is :00, allow :30 (30 minutes later)
			if (fromMinute === '00') {
				return ['30'];
			}
			// If from is :30, no valid minutes in same hour (would need next hour)
			return [];
		}
		// For different hours, allow all minute options
		return minuteOptions;
	};

	// Scroll to bottom of card when dropdown opens (desktop only, set_time tab)
	const handleSelectClick = () => {
		if (!isMobile && activeTab === 'set_time' && cardContainerRef.current) {
			// Use requestAnimationFrame for immediate scroll, then check again after a short delay
			// to handle any expansion that happens after dropdown opens
			requestAnimationFrame(() => {
				if (cardContainerRef.current) {
					cardContainerRef.current.scrollTop = cardContainerRef.current.scrollHeight;
				}
				// Double-check after a very short delay in case card expands further
				setTimeout(() => {
					if (cardContainerRef.current) {
						cardContainerRef.current.scrollTop = cardContainerRef.current.scrollHeight;
					}
				}, 150);
			});
		}
	};

	return (
		<div
			ref={cardContainerRef}
			id="preferred-time-selector-container"
			className={`
				rounded-[16px]
				bg-[#ffffff]
				h-fit
				md:h-[460px]
				md:max-h-[460px]
				px-5 lg:px-8
				py-4 lg:py-9
				flex
				flex-col
				overflow-visible
				relative
				md:min-h-0
				md:overflow-y-auto
				md:overflow-x-visible
				custom-scrollbar
				${additionalClassName || ''}
			`}
		>
			{/* Tab buttons */}
			<div className="flex gap-2 mb-6 md:-mt-5 flex-shrink-0">
				<button
					type="button"
					onClick={() => handleTabChange('time_range')}
					className={`
						flex-1
						py-1
						px-4
						rounded-[8px]
						font-medium
						text-[15px]
						transition-all
						duration-200
						border-[1px]
						border-solid
						border-[#0A9281]
						outline-none
						${activeTab === 'time_range' 
							? 'bg-[#0A9281] text-white shadow-[0_2px_8px_rgba(10,146,129,0.25)]' 
							: 'bg-white text-[#4B5B4B] shadow-[0_2px_8px_rgba(0,0,0,0.08)]'
						}
					`}
				>
					{translate(language, 'choose_doctor', 'preferred_time/time_range/title')}
				</button>
				<button
					type="button"
					onClick={() => handleTabChange('set_time')}
					className={`
						flex-1
						py-1
						px-4
						rounded-[8px]
						font-medium
						text-[15px]
						transition-all
						duration-200
						border-[1px]
						border-solid
						border-[#0A9281]
						outline-none
						${activeTab === 'set_time' 
							? 'bg-[#0A9281] text-white shadow-[0_2px_8px_rgba(10,146,129,0.25)]' 
							: 'bg-white text-[#4B5B4B] shadow-[0_2px_8px_rgba(0,0,0,0.08)]'
						}
					`}
				>
					{translate(language, 'choose_doctor', 'preferred_time/set_time/title')}
				</button>
			</div>

			<Skeletonize
				isLoading={isLoading}
				className='h-[24px] md:h-[26px] mb-[21px] md:mb-[19px] flex-shrink-0'
				animation='syncWave pulse'
			>
				<h3
					id="preferred-time-selector-title"
					className="
						leading-[1.3]
						text-[21px] lg:text-[23px]  
						font-medium
						text-[#000000]
						capitalize
						mb-2
						animate-fade-in-up-10
					"
				>
					{translate(language, 'choose_doctor', 'preferred_time/select_time')}
				</h3>
				<p className="text-[14px] text-[#4B5B4B] mb-4">
					{translate(language, 'choose_doctor', 'preferred_time/availability_description')}
				</p>
			</Skeletonize>

			{/* Time Range Tab */}
			{activeTab === 'time_range' && (
				<div className="flex-1 overflow-x-visible pr-2 md:pr-4">
					{selectedDate && (
						<p className="text-[16px] font-medium text-[#000000] mb-4">
							{translate(language, 'choose_doctor', 'preferred_time/select_slot_for_date').replace('{date}', formatDateStamp(selectedDate, { showYear: false }))}
						</p>
					)}
					<div className="grid grid-cols-2 gap-4">
						{timeRangeOptions.map((option, index) => {
							const isFlexible = option.label === translate(language, 'choose_doctor', 'preferred_time/time_range/flexible_option');
							return (
								<button
									key={index}
									type="button"
									onClick={() => handleTimeRangeSelection(option)}
									className={`
										flex
										items-center
										justify-center
										gap-2
										min-h-[48px]
										py-3
										px-4
										rounded-[12px]
										font-medium
										text-[13px]
										transition-all
										duration-200
										border-[1px]
										border-solid
										w-full
										${isTimeRangeSelected(option)
											? 'bg-[#0A9281] text-white border-transparent shadow-[0_2px_8px_rgba(10,146,129,0.25)]'
											: 'bg-white text-[#000000] border-gray-200 hover:border-[#0A9281] hover:text-[#0A9281] shadow-[0_2px_8px_rgba(0,0,0,0.08)]'
										}
									`}
								>
									{!isFlexible && (
										<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
											<path d="M8 0C3.58 0 0 3.58 0 8s3.58 8 8 8 8-3.58 8-8-3.58-8-8-8zm0 14c-3.31 0-6-2.69-6-6s2.69-6 6-6 6 2.69 6 6-2.69 6-6 6zm.5-9H7v4l3.5 2.1.8-1.2-2.8-1.6V5z" fill="currentColor"/>
										</svg>
									)}
									<span>{option.label}</span>
								</button>
							);
						})}
					</div>
				</div>
			)}

			{/* Set Time Tab */}
			{activeTab === 'set_time' && (
				<div className="flex-1 space-y-6 overflow-visible md:overflow-x-visible">
					{selectedDate && (
						<p className="text-[16px] font-medium text-[#000000] mb-4">
							{translate(language, 'choose_doctor', 'preferred_time/select_slot_for_date').replace('{date}', formatDateStamp(selectedDate, { showYear: false }))}
						</p>
					)}
					
					{/* Current Time Display - Mobile */}
					<div className={`
						md:hidden
						flex
						items-center
						justify-center
						gap-2
						py-3
						px-4
						rounded-[12px]
						border-[1px]
						border-solid
						bg-white
						border-[#CAD2CA]
					`}>
						<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
							<path d="M8 0C3.58 0 0 3.58 0 8s3.58 8 8 8 8-3.58 8-8-3.58-8-8-8zm0 14c-3.31 0-6-2.69-6-6s2.69-6 6-6 6 2.69 6 6-2.69 6-6 6zm.5-9H7v4l3.5 2.1.8-1.2-2.8-1.6V5z" fill="#000000"/>
						</svg>
						<span className="text-[15px] font-medium text-[#000000]">
							{fromHour && fromMinute && toHour && toMinute
								? `${fromHour}:${fromMinute} - ${toHour}:${toMinute}`
								: selectedTime && selectedTime.start && selectedTime.end
								? `${getTimePortion(selectedTime.start).substring(0, 5)} - ${getTimePortion(selectedTime.end).substring(0, 5)}`
									: `${fromHour || '00'}:${fromMinute || '00'} - ${toHour || '00'}:${toMinute || '00'}`
							}
						</span>
					</div>

					{/* Desktop: From/To Time Selectors */}
					<div className="hidden md:block space-y-4 relative md:min-h-0" style={{ overflow: 'visible' }}>
						{/* Current Time Display - Desktop */}
						<div 
							className={`
								hidden
								md:flex
								items-center
								justify-center
								gap-2
								py-3
								px-4
								rounded-[12px]
								border-[1px]
								border-solid
								bg-white
								border-[#CAD2CA]
							`}
						>
							<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
								<path d="M8 0C3.58 0 0 3.58 0 8s3.58 8 8 8 8-3.58 8-8-3.58-8-8-8zm0 14c-3.31 0-6-2.69-6-6s2.69-6 6-6 6 2.69 6 6-2.69 6-6 6zm.5-9H7v4l3.5 2.1.8-1.2-2.8-1.6V5z" fill="#000000"/>
							</svg>
							<span className="text-[15px] font-medium text-[#000000]">
								{fromHour && fromMinute && toHour && toMinute
									? `${fromHour}:${fromMinute} - ${toHour}:${toMinute}`
									: selectedTime && selectedTime.start && selectedTime.end
										? `${getTimePortion(selectedTime.start).substring(0, 5)} - ${getTimePortion(selectedTime.end).substring(0, 5)}`
										: `${fromHour || '00'}:${fromMinute || '00'} - ${toHour || '00'}:${toMinute || '00'}`
								}
							</span>
						</div>

						{/* From Time */}
						<div className="relative">
							<label className="block text-[13px] font-medium text-[#4B5B4B] mb-1.5">
								{translate(language, 'choose_doctor', 'preferred_time/set_time/select_from')}
							</label>
							<div className="flex gap-2">
								<div className="flex-1 relative z-50">
									<label className="block text-[11px] text-[#4B5B4B] mb-1">Hour</label>
									<div 
										style={{ boxShadow: '0 2px 8px rgba(0, 0, 0, 0.12)', borderRadius: '10px', overflow: 'visible' }}
										onClick={handleSelectClick}
									>
										<Select
											options={hourOptions.map(h => ({ value: h, id: h })) as any}
											placeholder="Hour"
											initialValue={fromHour ? { value: fromHour, id: fromHour } : {}}
											onChangeCallback={({ option }) => {
												const hour = option.value;
												const newMinute = hour === '22' ? '00' : fromMinute;
												handleDesktopFromTimeChange(hour, newMinute);
								}}
											errorMode={false}
											id="from-hour-select"
											style={{
												border: 'none',
												height: '40px',
												width: '100%',
												borderRadius: '10px',
												backgroundColor: 'white',
												margin: '0px'
											}}
										/>
									</div>
								</div>
								<div className="flex-1 relative" style={{ overflow: 'visible' }}>
									<label className="block text-[11px] text-[#4B5B4B] mb-1">Minute</label>
									<div 
										style={{ boxShadow: '0 2px 8px rgba(0, 0, 0, 0.12)', borderRadius: '10px', overflow: 'visible' }}
										onClick={handleSelectClick}
									>
										<Select
											options={(fromHour === '22' ? ['00'] : minuteOptions).map(m => ({ value: m, id: m })) as any}
											placeholder="Minute"
											initialValue={fromMinute ? { value: fromMinute, id: fromMinute } : {}}
											onChangeCallback={({ option }) => {
												handleDesktopFromTimeChange(fromHour, option.value);
											}}
											disabled={!fromHour}
											errorMode={false}
											id="from-minute-select"
											style={{
												border: 'none',
												height: '40px',
												width: '100%',
												borderRadius: '10px',
												backgroundColor: 'white',
												margin: '0px'
											}}
							/>
									</div>
								</div>
							</div>
						</div>

						{/* To Time */}
						<div className="relative">
							<label className="block text-[13px] font-medium text-[#4B5B4B] mb-1.5">
								{translate(language, 'choose_doctor', 'preferred_time/set_time/select_to')}
							</label>
							<div className="flex gap-2" style={{ overflow: 'visible' }}>
								<div className="flex-1 relative" style={{ overflow: 'visible' }}>
									<label className="block text-[11px] text-[#4B5B4B] mb-1">Hour</label>
									<div 
										style={{ boxShadow: '0 2px 8px rgba(0, 0, 0, 0.12)', borderRadius: '10px', position: 'relative', overflow: 'visible' }}
										onClick={handleSelectClick}
									>
										<Select
											options={getAvailableToHours().map(h => ({ value: h, id: h })) as any}
											placeholder="Hour"
											initialValue={toHour ? { value: toHour, id: toHour } : {}}
											onChangeCallback={({ option }) => {
												const hour = option.value;
												const newMinute = (hour === '22' && fromHour === '22' && fromMinute === '00') ? '30' : toMinute;
												handleDesktopToTimeChange(hour, newMinute);
								}}
											disabled={getAvailableToHours().length === 0}
											errorMode={false}
											id="to-hour-select"
											style={{
												border: 'none',
												height: '40px',
												width: '100%',
												borderRadius: '10px',
												backgroundColor: 'white',
												margin: '0px'
											}}
										/>
									</div>
								</div>
								<div className="flex-1 relative" style={{ overflow: 'visible' }}>
									<label className="block text-[11px] text-[#4B5B4B] mb-1">Minute</label>
									<div 
										style={{ boxShadow: '0 2px 8px rgba(0, 0, 0, 0.12)', borderRadius: '10px', position: 'relative', overflow: 'visible' }}
										onClick={handleSelectClick}
									>
										<Select
											options={getAvailableToMinutes().map(m => ({ value: m, id: m })) as any}
											placeholder="Minute"
											initialValue={toMinute ? { value: toMinute, id: toMinute } : {}}
											onChangeCallback={({ option }) => {
												handleDesktopToTimeChange(toHour, option.value);
											}}
											disabled={!toHour || getAvailableToMinutes().length === 0}
											errorMode={false}
											id="to-minute-select"
											style={{
												border: 'none',
												height: '40px',
												width: '100%',
												borderRadius: '10px',
												backgroundColor: 'white',
												margin: '0px'
											}}
										/>
									</div>
								</div>
							</div>
						</div>
					</div>

					{/* Mobile: Time Selectors */}
					<div className="md:hidden space-y-6">
						{/* From Time */}
						<div>
							<label className="block text-[14px] font-medium text-[#4B5B4B] mb-2">
								{translate(language, 'choose_doctor', 'preferred_time/set_time/select_from')}
							</label>
							<ScrollableTimeSelector
								value={fromHour && fromMinute ? `${fromHour}:${fromMinute}` : ''}
								options={hourOptions.flatMap(h => {
									// For hour 22, only allow 00 for "From" time
									const minutes = h === '22' ? ['00'] : minuteOptions;
									return minutes.map(m => `${h}:${m}`);
								})}
								onChange={(time) => {
									const [hour, minute] = time.split(':');
									handleFromTimeChange(hour, minute);
								}}
								label={translate(language, 'choose_doctor', 'preferred_time/set_time/select_time_from')}
							/>
						</div>

						{/* To Time */}
						<div>
							<label className="block text-[14px] font-medium text-[#4B5B4B] mb-2">
								{translate(language, 'choose_doctor', 'preferred_time/set_time/select_to')}
							</label>
							<ScrollableTimeSelector
								value={toHour && toMinute ? `${toHour}:${toMinute}` : ''}
								options={getAvailableToHours().flatMap(h => 
									getAvailableToMinutes()
										.map(m => `${h}:${m}`)
										.filter(time => {
											if (!fromHour || !fromMinute) return true;
											const [hour, minute] = time.split(':');
											const fromTime = formatTimeForComparison(fromHour, fromMinute);
											const toTime = formatTimeForComparison(hour, minute);
											return toTime > fromTime;
										})
								)}
								onChange={(time) => {
									const [hour, minute] = time.split(':');
									handleToTimeChange(hour, minute);
								}}
								label={translate(language, 'choose_doctor', 'preferred_time/set_time/select_time_to')}
								disabled={getAvailableToHours().length === 0}
							/>
						</div>
					</div>

					{/* Confirm Time Button - Mobile Only */}
					<button
						type="button"
						onClick={(e) => {
							e.preventDefault();
							e.stopPropagation();
							if (isSetTimeValid() && fromHour && fromMinute && toHour && toMinute) {
								onTimeSelection(buildTimeSlot(fromHour, fromMinute, toHour, toMinute));
							}
						}}
						disabled={!isSetTimeValid()}
						className={`
							md:hidden
							w-full
							py-3
							px-4
							rounded-[12px]
							font-medium
							text-[15px]
							transition-all
							duration-200
							border-none
							${isSetTimeValid()
								? 'bg-[#0A9281] text-white hover:bg-[#0A8271] shadow-[0_2px_8px_rgba(10,146,129,0.25)]'
								: 'bg-gray-300 text-gray-500 cursor-not-allowed shadow-[0_1px_3px_rgba(0,0,0,0.1)]'
							}
						`}
					>
						{translate(language, 'choose_doctor', 'preferred_time/confirm_time')}
					</button>
				</div>
			)}
		</div>
	);
}

export default PreferredTimeSelector;

